import React from 'react';
import './Document.css';

const PrivacyPolicy = () => {
  return (
    <div className="document-container">
      <div className="document-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated"><strong>Last Updated: January 2025</strong></p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy explains how the Jo Jo Nickname Changer Bot ("the Bot", "we", "us", "our") collects, uses, stores, and protects your information when you use our Discord bot service.
          </p>
          <p>
            By using the Bot, you agree to the collection and use of information in accordance with this Privacy Policy.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Automatically Collected Information</h3>
          <p>When you use the Bot, we automatically collect and store:</p>
          
          <p><strong>User Information:</strong></p>
          <ul>
            <li>Discord User ID</li>
            <li>Discord Username and Tag</li>
            <li>Server (Guild) ID where the Bot is used</li>
            <li>Channel ID where nickname requests are made</li>
          </ul>

          <p><strong>Usage Information:</strong></p>
          <ul>
            <li>Nickname change requests and their content</li>
            <li>Timestamps of all actions (requests, approvals, rejections)</li>
            <li>Approval/rejection decisions and reasons</li>
            <li>Cooldown and usage patterns</li>
          </ul>

          <p><strong>Moderation Data:</strong></p>
          <ul>
            <li>Filtered words (global and per-server)</li>
            <li>Rejected nickname requests and reasons</li>
            <li>Moderator actions and decisions</li>
            <li>Monitoring and reporting data</li>
          </ul>

          <p><strong>History Data:</strong></p>
          <ul>
            <li>Previous nicknames (last 10 changes per user)</li>
            <li>Nickname change history with timestamps</li>
            <li>Old and new nickname values</li>
          </ul>

          <h3>2.2 Information You Provide</h3>
          <p>You may provide information through:</p>
          <ul>
            <li>Nickname change requests (the nickname text you submit)</li>
            <li>Server configuration (channel settings, moderator roles)</li>
            <li>Filter word additions (if you're a server administrator)</li>
          </ul>

          <h3>2.3 Information from Discord</h3>
          <p>The Bot receives information from Discord's API, including:</p>
          <ul>
            <li>User profile information (username, avatar, discriminator)</li>
            <li>Server information (server name, member count)</li>
            <li>Channel information</li>
            <li>Role and permission information</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          
          <h3>3.1 Service Provision</h3>
          <p>We use your information to:</p>
          <ul>
            <li>Process nickname change requests</li>
            <li>Manage approval workflows</li>
            <li>Enforce word filters and moderation</li>
            <li>Track nickname history</li>
            <li>Provide monitoring and reporting features</li>
            <li>Maintain cooldown systems</li>
          </ul>

          <h3>3.2 Data Storage</h3>
          <ul>
            <li>All data is stored locally in JSON files on the Bot's server</li>
            <li>Data is stored in the <code>./data/</code> directory</li>
            <li>No data is transmitted to third-party services (except Discord's API)</li>
          </ul>

          <h3>3.3 Data Retention</h3>
          <ul>
            <li><strong>Nickname History:</strong> Retained for the last 10 changes per user</li>
            <li><strong>Pending Requests:</strong> Retained until approved, rejected, or expired (24 hours)</li>
            <li><strong>Monitoring Data:</strong> Retained for up to 100 entries</li>
            <li><strong>Configuration Data:</strong> Retained indefinitely while the Bot is in use</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Storage and Security</h2>
          
          <h3>4.1 Storage Location</h3>
          <ul>
            <li>All data is stored on the Bot owner's server</li>
            <li>Data is stored in plain JSON files</li>
            <li>Files are stored in a <code>./data/</code> directory</li>
          </ul>

          <h3>4.2 Security Measures</h3>
          <ul>
            <li>Access to data files is restricted to the Bot owner</li>
            <li>The Bot uses Discord's secure API for communication</li>
            <li>No data is transmitted over unencrypted channels</li>
          </ul>

          <h3>4.3 Data Security Limitations</h3>
          <p>Please note:</p>
          <ul>
            <li>JSON files are not encrypted</li>
            <li>Data is stored in plain text format</li>
            <li>Server security depends on the Bot owner's infrastructure</li>
            <li>We cannot guarantee absolute security against unauthorized access</li>
          </ul>
        </section>

        <section>
          <h2>5. Data Sharing and Disclosure</h2>
          
          <h3>5.1 With Discord</h3>
          <ul>
            <li>We share necessary information with Discord's API to provide Bot functionality</li>
            <li>This includes user IDs, server IDs, and nickname changes</li>
            <li>Discord's Privacy Policy applies to data shared with Discord</li>
          </ul>

          <h3>5.2 With Server Administrators</h3>
          <p>Server administrators can view:</p>
          <ul>
            <li>Nickname change requests in their server</li>
            <li>Nickname history for users in their server</li>
            <li>Server-specific filter words</li>
            <li>Monitoring data for their server</li>
          </ul>

          <h3>5.3 With Other Users</h3>
          <p>Other users in your server may see:</p>
          <ul>
            <li>Your nickname change requests (in the configured channel)</li>
            <li>Approval/rejection messages</li>
            <li>Your current and previous nicknames (if they have permission)</li>
          </ul>

          <h3>5.4 No Third-Party Sharing</h3>
          <ul>
            <li>We do not sell your data</li>
            <li>We do not share your data with third-party services</li>
            <li>We do not use your data for advertising or marketing</li>
          </ul>
        </section>

        <section>
          <h2>6. Your Rights and Choices</h2>
          
          <h3>6.1 Access to Your Data</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Request information about data we have collected about you</li>
            <li>View your nickname history (using <code>/nickhistory</code> command)</li>
            <li>See what data is stored in our systems</li>
          </ul>

          <h3>6.2 Data Deletion</h3>
          <p>You can request deletion of your data by:</p>
          <ul>
            <li>Contacting the Bot owner</li>
            <li>Requesting removal from a server (server administrators can remove server-specific data)</li>
            <li>Note: Some data may be retained for operational or legal reasons</li>
          </ul>

          <h3>6.3 Opt-Out Options</h3>
          <ul>
            <li>You can stop using the Bot at any time</li>
            <li>Server administrators can remove the Bot from their server</li>
            <li>Removing the Bot will stop new data collection for that server</li>
          </ul>
        </section>

        <section>
          <h2>7. Children's Privacy</h2>
          
          <h3>7.1 Age Requirements</h3>
          <ul>
            <li>Discord requires users to be at least 13 years old</li>
            <li>We comply with Discord's age requirements</li>
            <li>We do not knowingly collect data from users under 13</li>
          </ul>

          <h3>7.2 COPPA Compliance</h3>
          <ul>
            <li>If you are under 13, please do not use this Bot</li>
            <li>If we discover we have collected data from a user under 13, we will delete it</li>
          </ul>
        </section>

        <section>
          <h2>8. International Data Transfers</h2>
          
          <h3>8.1 Data Location</h3>
          <ul>
            <li>Data is stored on servers controlled by the Bot owner</li>
            <li>Data may be stored in a location different from your country</li>
            <li>By using the Bot, you consent to data transfer to the Bot owner's location</li>
          </ul>

          <h3>8.2 GDPR Considerations</h3>
          <p>If you are in the European Economic Area (EEA):</p>
          <ul>
            <li>You have the right to access, rectify, or erase your data</li>
            <li>You have the right to data portability</li>
            <li>You have the right to object to processing</li>
            <li>Contact the Bot owner to exercise these rights</li>
          </ul>
        </section>

        <section>
          <h2>9. Cookies and Tracking</h2>
          
          <h3>9.1 No Cookies</h3>
          <ul>
            <li>The Bot does not use cookies</li>
            <li>The Bot does not use web tracking technologies</li>
            <li>The Bot only uses Discord's API for functionality</li>
          </ul>
        </section>

        <section>
          <h2>10. Third-Party Services</h2>
          
          <h3>10.1 Discord</h3>
          <ul>
            <li>The Bot is built on Discord's platform</li>
            <li>Discord's Privacy Policy applies to data shared with Discord</li>
            <li>We are not responsible for Discord's data practices</li>
          </ul>

          <h3>10.2 Monitoring Services</h3>
          <ul>
            <li>The Bot may use heartbeat monitoring services</li>
            <li>These services may receive basic status information</li>
            <li>No personal user data is shared with monitoring services</li>
          </ul>
        </section>

        <section>
          <h2>11. Data Breach Notification</h2>
          
          <h3>11.1 Notification</h3>
          <p>If we become aware of a data breach:</p>
          <ul>
            <li>We will notify affected users as soon as possible</li>
            <li>We will report the breach to relevant authorities if required</li>
            <li>We will take steps to mitigate the breach</li>
          </ul>

          <h3>11.2 Limitations</h3>
          <ul>
            <li>Notification may be delayed if required by law enforcement</li>
            <li>We may not be able to identify all affected users</li>
            <li>Notification will be made through available channels (Discord, GitHub)</li>
          </ul>
        </section>

        <section>
          <h2>12. Changes to This Privacy Policy</h2>
          
          <h3>12.1 Updates</h3>
          <ul>
            <li>We may update this Privacy Policy from time to time</li>
            <li>The "Last Updated" date at the top indicates when changes were made</li>
            <li>Material changes will be communicated to users</li>
          </ul>

          <h3>12.2 Continued Use</h3>
          <ul>
            <li>Continued use of the Bot after policy changes constitutes acceptance</li>
            <li>If you do not agree with changes, please stop using the Bot</li>
          </ul>
        </section>

        <section>
          <h2>13. Server Administrator Responsibilities</h2>
          
          <h3>13.1 Data Control</h3>
          <p>Server administrators:</p>
          <ul>
            <li>Control what data is collected in their server</li>
            <li>Can configure which channels the Bot monitors</li>
            <li>Can set up filtering and moderation rules</li>
            <li>Are responsible for complying with applicable privacy laws</li>
          </ul>

          <h3>13.2 User Notification</h3>
          <p>Server administrators should:</p>
          <ul>
            <li>Inform users that the Bot is in use</li>
            <li>Explain what data is collected</li>
            <li>Provide access to this Privacy Policy</li>
            <li>Respect user privacy rights</li>
          </ul>
        </section>

        <section>
          <h2>14. Contact Information</h2>
          
          <h3>14.1 Privacy Inquiries</h3>
          <p>For questions about this Privacy Policy or data practices:</p>
          <ul>
            <li><strong>Bot Owner:</strong> Joynal Bokhsho</li>
            <li><strong>GitHub:</strong> <a href="https://github.com/joynalbokhsho" target="_blank" rel="noopener noreferrer">@joynalbokhsho</a></li>
            <li><strong>Discord:</strong> Contact through the Bot's support channels</li>
          </ul>

          <h3>14.2 Data Requests</h3>
          <p>To request access, deletion, or modification of your data:</p>
          <ul>
            <li>Contact the Bot owner through available channels</li>
            <li>Provide your Discord User ID for verification</li>
            <li>Allow reasonable time for processing (typically 7-14 days)</li>
          </ul>
        </section>

        <section>
          <h2>15. Compliance</h2>
          
          <h3>15.1 Legal Compliance</h3>
          <ul>
            <li>We strive to comply with applicable privacy laws</li>
            <li>This includes GDPR (for EEA users), CCPA (for California users), and other relevant regulations</li>
            <li>We may update this policy to maintain compliance</li>
          </ul>

          <h3>15.2 Discord Compliance</h3>
          <ul>
            <li>We comply with Discord's Developer Terms of Service</li>
            <li>We follow Discord's API usage guidelines</li>
            <li>We respect Discord's privacy requirements</li>
          </ul>
        </section>

        <section>
          <h2>16. Disclaimer</h2>
          
          <h3>16.1 No Guarantees</h3>
          <ul>
            <li>While we take privacy seriously, we cannot guarantee absolute security</li>
            <li>Data is stored in plain text files</li>
            <li>Security depends on server infrastructure</li>
          </ul>

          <h3>16.2 Third-Party Risks</h3>
          <ul>
            <li>We are not responsible for Discord's data practices</li>
            <li>We are not responsible for server administrator actions</li>
            <li>We are not responsible for data breaches caused by third parties</li>
          </ul>
        </section>

        <section>
          <h2>17. Consent</h2>
          <p>By using the Jo Jo Nickname Changer Bot, you consent to:</p>
          <ul>
            <li>The collection and use of information as described in this Privacy Policy</li>
            <li>The storage of your data as described</li>
            <li>The sharing of data as necessary for Bot functionality</li>
          </ul>
        </section>

        <section>
          <h2>18. Acknowledgment</h2>
          <p>You acknowledge that:</p>
          <ul>
            <li>You have read and understood this Privacy Policy</li>
            <li>You consent to our data practices</li>
            <li>You understand the limitations of our security measures</li>
            <li>You are responsible for the content you submit through the Bot</li>
          </ul>
        </section>

        <div className="document-footer">
          <p><strong>By using the Jo Jo Nickname Changer Bot, you acknowledge that you have read and understood this Privacy Policy.</strong></p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

